import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../API/api';

// Enhanced utility function for retrying API requests with jitter
const retryRequest = async (fn, maxRetries = 5, initialDelay = 1000) => {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      if (attempt === maxRetries) throw error;
      const delay = initialDelay * Math.pow(2, attempt - 1) + Math.random() * 1000; // Exponential backoff with jitter
      console.log(`Retry attempt ${attempt}/${maxRetries} failed. Retrying in ${delay}ms...`, error.message);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }
};

// Fetch Assets
export const fetchAssets = createAsyncThunk(
  'assets/fetchAssets',
  async ({ forceRefresh = false } = {}, { rejectWithValue, getState }) => {
    const state = getState();
    if (!forceRefresh && state.assets.assets.length > 0 && Date.now() - state.assets.lastFetched.assets < 5 * 60 * 1000) {
      console.log('Using cached assets');
      return state.assets.assets;
    }
    try {
      console.log('Fetching assets from /assets...');
      const response = await retryRequest(() => api.get('/assets/assets', { timeout: 30000 }));
      console.log('Assets fetched:', response.data);
      if (!Array.isArray(response.data)) {
        throw new Error('Expected an array of assets');
      }
      return response.data;
    } catch (error) {
      console.error('Error fetching assets:', error);
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch assets');
    }
  }
);

// Fetch Spot Trading Pairs
export const fetchSpotTradingPairs = createAsyncThunk(
  'assets/fetchSpotTradingPairs',
  async ({ forceRefresh = false } = {}, { rejectWithValue, getState }) => {
    const state = getState();
    if (!forceRefresh && state.assets.spotTradingPairs.length > 0 && Date.now() - state.assets.lastFetched.spotTradingPairs < 5 * 60 * 1000) {
      console.log('Using cached spot trading pairs');
      return state.assets.spotTradingPairs;
    }
    try {
      console.log('Fetching spot trading pairs from /assets/spot/trading-pairs...');
      const response = await retryRequest(() => api.get('/assets/spot/trading-pairs', { timeout: 30000 }));
      console.log('Spot trading pairs fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching spot trading pairs:', error);
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch spot trading pairs');
    }
  }
);

// Fetch Order Book
export const fetchOrderBook = createAsyncThunk(
  'assets/fetchOrderBook',
  async (symbol, { rejectWithValue }) => {
    try {
      console.log('Fetching spot order book from API for:', symbol);
      const response = await retryRequest(() => api.get(`/assets/spot/order-book/${symbol}`, { timeout: 10000 }));
      console.log('Spot order book fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching spot order book:', error);
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch spot order book');
    }
  }
);

// Fetch Futures Order Book
export const fetchFuturesOrderBook = createAsyncThunk(
  'assets/fetchFuturesOrderBook',
  async (symbol, { rejectWithValue, getState }) => {
    if (!symbol || typeof symbol !== 'string' || symbol.trim() === '') {
      console.error('Invalid symbol provided:', symbol);
      return rejectWithValue('Invalid trading pair symbol');
    }
    try {
      console.log('Fetching futures order book from API for:', symbol);
      const response = await retryRequest(() => api.get(`/assets/futures/order-book/${symbol}`, { timeout: 30000 }));
      console.log('Futures order book fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching futures order book:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url,
      });
      const state = getState();
      if (state.assets.futuresOrderBook.bids.length > 0 || state.assets.futuresOrderBook.asks.length > 0) {
        console.log('Using cached futures order book data as fallback');
        return state.assets.futuresOrderBook;
      }
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch futures order book');
    }
  }
);

// Fetch Total Balances
export const fetchTotalBalances = createAsyncThunk(
  'assets/fetchTotalBalances',
  async (_, { rejectWithValue }) => {
    try {
      console.log('Fetching total balances from /wallets/total-balances...');
      const response = await retryRequest(() => api.get('/wallets/total-balances', { timeout: 30000 }));
      console.log('Total balances fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching total balances:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch total balances');
    }
  }
);

// Fetch Spot Balances
export const fetchSpotBalances = createAsyncThunk(
  'assets/fetchSpotBalances',
  async (_, { rejectWithValue }) => {
    try {
      console.log('Fetching spot balances from /wallets/spot-balances...');
      const response = await retryRequest(() => api.get('/wallets/spot-balances', { timeout: 30000 }));
      console.log('Spot balances fetched:', response.data);
      return response.data.balances || [];
    } catch (error) {
      console.error('Error fetching spot balances:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch spot balances');
    }
  }
);

// Fetch Futures Balances
export const fetchFuturesBalances = createAsyncThunk(
  'assets/fetchFuturesBalances',
  async (_, { rejectWithValue }) => {
    try {
      console.log('Fetching futures balances from /wallets/futures-balances...');
      const response = await retryRequest(() => api.get('/wallets/futures-balances', { timeout: 30000 }));
      console.log('Futures balances fetched:', response.data);
      return response.data.balances || [];
    } catch (error) {
      console.error('Error fetching futures balances:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch futures balances');
    }
  }
);

// Fetch AI Balances
export const fetchAIBalances = createAsyncThunk(
  'assets/fetchAIBalances',
  async (_, { rejectWithValue }) => {
    try {
      console.log('Fetching AI balances from /wallets/ai-balances...');
      const response = await retryRequest(() => api.get('/wallets/ai-balances', { timeout: 30000 }));
      console.log('AI balances fetched:', response.data);
      return response.data.balances || [];
    } catch (error) {
      console.error('Error fetching AI balances:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch AI balances');
    }
  }
);

// Fetch Wallet
export const fetchWallet = createAsyncThunk(
  'assets/fetchWallet',
  async ({ forceRefresh = false } = {}, { rejectWithValue, getState }) => {
    const state = getState();
    if (!forceRefresh && state.assets.walletInitialized && Date.now() - state.assets.lastFetched.wallet < 5 * 60 * 1000) {
      console.log('Using cached wallet status');
      return { status: state.assets.walletInitialized };
    }
    try {
      console.log('Fetching wallet from /wallets/check-wallet...');
      const response = await retryRequest(() => api.get('/wallets/check-wallet', { timeout: 30000 }));
      console.log('Wallet fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching wallet:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch wallet');
    }
  }
);

// Initialize Wallet
export const initializeWallet = createAsyncThunk(
  'assets/initializeWallet',
  async (_, { rejectWithValue, dispatch, getState }) => {
    const { walletInitialized } = getState().assets;
    if (walletInitialized) {
      console.log('Wallet already initialized, skipping initialization...');
      dispatch(fetchWalletIds({ forceRefresh: true }));
      dispatch(fetchFuturesBalances());
      return { status: 'already_initialized' };
    }
    try {
      console.log('Initializing wallet via /wallets/initialize-wallet...');
      const response = await retryRequest(() => api.post('/wallets/initialize-wallet', {}, { timeout: 30000 }));
      console.log('Wallet initialized:', response.data);
      dispatch(fetchTotalBalances());
      dispatch(fetchSpotBalances());
      dispatch(fetchFuturesBalances());
      dispatch(fetchAIBalances());
      dispatch(fetchWalletIds({ forceRefresh: true }));
      return response.data;
    } catch (error) {
      console.error('Error initializing wallet:', error);
      if (error.response?.status === 400 && error.response?.data?.message.includes('Spot Wallet')) {
        console.log('Spot wallet exists, fetching existing wallet IDs and balances...');
        dispatch(fetchWalletIds({ forceRefresh: true }));
        dispatch(fetchFuturesBalances());
        return { status: 'already_initialized' };
      }
      return rejectWithValue(error.response?.data?.message || 'Failed to initialize wallet');
    }
  }
);

// Fetch Wallet IDs
export const fetchWalletIds = createAsyncThunk(
  'assets/fetchWalletIds',
  async ({ userId, forceRefresh = false } = {}, { rejectWithValue, getState }) => {
    const state = getState();
    if (!forceRefresh && state.assets.walletIds.spotWalletId && Date.now() - state.assets.lastFetched.walletIds < 5 * 60 * 1000) {
      console.log('Using cached wallet IDs');
      return state.assets.walletIds;
    }
    try {
      console.log('Fetching wallet IDs from /wallets/wallet-ids:', userId);
      const response = await retryRequest(() => api.get(`/wallets/wallet-ids/${userId}`, { timeout: 30000 }));
      console.log('Wallet IDs fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching wallet IDs:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch wallet IDs');
    }
  }
);

// Create Transfer
export const createTransfer = createAsyncThunk(
  'assets/createTransfer',
  async (transferData, { rejectWithValue, dispatch }) => {
    try {
      console.log('Creating transfer:', transferData);
      const response = await retryRequest(() => api.post('/transfers', transferData, { timeout: 30000 }));
      console.log('Transfer created:', response.data);
      dispatch(fetchTotalBalances());
      dispatch(fetchSpotBalances());
      dispatch(fetchFuturesBalances());
      dispatch(fetchAIBalances());
      return response.data;
    } catch (error) {
      console.error('Error creating transfer:', error);
      return rejectWithValue(error.response?.data || 'Failed to create transfer');
    }
  }
);

// Get Transfer History
export const getTransferHistory = createAsyncThunk(
  'assets/getTransferHistory',
  async (userId, { rejectWithValue }) => {
    try {
      console.log(`Fetching transfer history from /transfers/history/${userId}...`);
      const response = await retryRequest(() => api.get(`/transfers/history/${userId}`, { timeout: 30000 }));
      console.log('Transfer history fetched:', response.data);
      return response.data || [];
    } catch (error) {
      console.error('Error fetching transfer:', error);
      return rejectWithValue(error.response?.data || 'Failed to fetch transfer history');
    }
  }
);

// Fetch Open Futures Orders
export const fetchOpenFuturesOrders = createAsyncThunk(
  'assets/fetchOpenFuturesOrders',
  async (userId, { rejectWithValue }) => {
    try {
      console.log(`Fetching open futures orders from /futures/open-orders/${userId}...`);
      const response = await retryRequest(() => api.get(`/futures/open-orders/${userId}`, { timeout: 30000 }));
      console.log('Open futures orders fetched:', response.data);
      return response.data || [];
    } catch (error) {
      console.error('Error fetching open futures orders:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch open futures orders');
    }
  }
);

// Fetch Futures Trade History
export const fetchFuturesTradeHistory = createAsyncThunk(
  'assets/fetchFuturesTradeHistory',
  async (userId, { rejectWithValue }) => {
    try {
      console.log(`Fetching futures trade history from /futures/history/${userId}...`);
      const response = await retryRequest(() => api.get(`/futures/history/${userId}`, { timeout: 30000 }));
      console.log('Futures trade history fetched:', response.data);
      return response.data || [];
    } catch (error) {
      console.error('Error fetching futures trade history:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch futures trade history');
    }
  }
);

// Fetch Futures Price
export const fetchFuturesPrice = createAsyncThunk(
  'assets/fetchFuturesPrice',
  async (symbol, { rejectWithValue }) => {
    try {
      console.log(`Fetching futures price from /futures/price/${symbol}...`);
      const response = await retryRequest(() => api.get(`/futures/price/${symbol}`, { timeout: 10000 }));
      console.log('Futures price fetched:', response.data);
      return { symbol, price: response.data.price };
    } catch (error) {
      console.error('Error fetching futures price:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch futures price');
    }
  }
);

// Place Futures Order
export const placeFuturesOrder = createAsyncThunk(
  'assets/placeFuturesOrder',
  async (orderData, { rejectWithValue, dispatch }) => {
    try {
      console.log('Placing futures order via /futures/trade...', orderData);
      const response = await retryRequest(() => api.post('/futures/trade', orderData, { timeout: 30000 }));
      console.log('Futures order placed successfully:', response.data);
      dispatch(fetchOpenFuturesOrders(orderData.user_id));
      dispatch(fetchFuturesBalances());
      return response.data;
    } catch (error) {
      console.error('Error placing futures order:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      const errorMessage = error.response?.data?.message || 'Failed to place futures order';
      return rejectWithValue(errorMessage);
    }
  }
);

const assetsSlice = createSlice({
  name: 'assets',
  initialState: {
    assets: [],
    spotTradingPairs: [],
    orderBook: { bids: [], asks: [], currentPrice: null, lastUpdateId: null },
    futuresOrderBook: { bids: [], asks: [], currentPrice: null, timestamp: null },
    totalBalances: { totalUSDT: 0, spotBalances: [], futuresBalances: [], aiBalances: [], todayPNL: 0 },
    spotBalances: [],
    futuresBalances: [],
    aiBalances: [],
    openFuturesOrders: [],
    futuresTradeHistory: [],
    coinPrices: {},
    assetsLoading: false,
    orderBookLoading: false,
    futuresOrderBookLoading: false,
    openFuturesOrdersLoading: false,
    futuresTradeHistoryLoading: false,
    futuresPriceLoading: false,
    error: null,
    walletInitialized: false,
    transferHistory: [],
    walletIds: { spotWalletId: null, futuresWalletId: null, aiWalletId: null },
    lastFetched: {
      assets: 0,
      totalBalances: 0,
      spotBalances: 0,
      futuresBalances: 0,
      aiBalances: 0,
      wallet: 0,
      walletIds: 0,
      spotTradingPairs: 0,
    },
  },
  reducers: {
    updateCoinPrice: (state, action) => {
      const { symbol, price } = action.payload;
      state.coinPrices[symbol] = price;
    },
    setCoinPrices: (state, action) => {
      const newPrices = action.payload;
      if (JSON.stringify(state.coinPrices) !== JSON.stringify(newPrices)) {
        state.coinPrices = newPrices;
      }
    },
    updateOrderBook: (state, action) => {
      state.orderBook = {
        bids: action.payload.bids || [],
        asks: action.payload.asks || [],
        currentPrice: action.payload.currentPrice,
        lastUpdateId: action.payload.lastUpdateId || state.orderBook.lastUpdateId,
      };
      state.orderBookLoading = false;
      state.error = null;
    },
    updateFuturesOrderBook: (state, action) => {
      state.futuresOrderBook = {
        bids: action.payload.bids || [],
        asks: action.payload.asks || [],
        currentPrice: action.payload.currentPrice || state.futuresOrderBook.currentPrice,
        timestamp: action.payload.timestamp || state.futuresOrderBook.timestamp,
      };
      state.futuresOrderBookLoading = false;
      state.error = null;
    },
    setOrderBookError: (state, action) => {
      state.error = action.payload;
      state.orderBookLoading = false;
      state.futuresOrderBookLoading = false;
    },
    handleFuturesLiquidation: (state, action) => {
      const { trade_id } = action.payload;
      state.openFuturesOrders = state.openFuturesOrders.filter((order) => order._id !== trade_id);
      state.error = `تم تصفية الصفقة ${trade_id}`;
    },
    setCachedData: (state, action) => {
      const { assets, totalBalances, spotBalances, futuresBalances, aiBalances, walletInitialized } = action.payload;
      state.assets = assets || state.assets;
      state.totalBalances = totalBalances || state.totalBalances;
      state.spotBalances = spotBalances || state.spotBalances;
      state.futuresBalances = futuresBalances || state.futuresBalances;
      state.aiBalances = aiBalances || state.aiBalances;
      state.walletInitialized = walletInitialized !== undefined ? walletInitialized : state.walletInitialized;
      state.lastFetched = {
        assets: Date.now(),
        totalBalances: Date.now(),
        spotBalances: Date.now(),
        futuresBalances: Date.now(),
        aiBalances: Date.now(),
        wallet: Date.now(),
        walletIds: Date.now(),
      };
    },
    updateSpotBalances: (state, action) => {
      console.log('Manually updating spot balances:', action.payload);
      state.spotBalances = action.payload;
      state.lastFetched.spotBalances = Date.now();
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAssets.pending, (state) => {
        console.log('fetchAssets pending');
        state.assetsLoading = true;
        state.error = null;
      })
      .addCase(fetchAssets.fulfilled, (state, action) => {
        console.log('fetchAssets fulfilled', action.payload);
        state.assetsLoading = false;
        state.assets = action.payload;
        state.lastFetched.assets = Date.now();
      })
      .addCase(fetchAssets.rejected, (state, action) => {
        console.log('fetchAssets rejected', action.payload);
        state.assetsLoading = false;
        state.error = action.payload || 'Request timed out. Please try again.';
      })
      .addCase(fetchSpotTradingPairs.pending, (state) => {
        console.log('fetchSpotTradingPairs pending');
        state.assetsLoading = true;
        state.error = null;
      })
      .addCase(fetchSpotTradingPairs.fulfilled, (state, action) => {
        console.log('fetchSpotTradingPairs fulfilled', action.payload);
        state.assetsLoading = false;
        state.spotTradingPairs = action.payload;
        state.lastFetched.spotTradingPairs = Date.now();
      })
      .addCase(fetchSpotTradingPairs.rejected, (state, action) => {
        console.log('fetchSpotTradingPairs rejected', action.payload);
        state.assetsLoading = false;
        state.error = action.payload || 'Request timed out.';
      })
      .addCase(fetchOrderBook.pending, (state) => {
        console.log('fetchOrderBook pending');
        state.orderBookLoading = true;
        state.error = null;
      })
      .addCase(fetchOrderBook.fulfilled, (state, action) => {
        console.log('fetchOrderBook fulfilled', action.payload);
        state.orderBookLoading = false;
        state.orderBook = {
          bids: action.payload.bids || [],
          asks: action.payload.asks || [],
          currentPrice: action.payload.currentPrice,
          lastUpdateId: action.payload.lastUpdateId,
        };
      })
      .addCase(fetchOrderBook.rejected, (state, action) => {
        console.log('fetchOrderBook rejected', action.payload);
        state.orderBookLoading = false;
        state.error = action.payload || 'Request timed out.';
      })
      .addCase(fetchFuturesOrderBook.pending, (state) => {
        console.log('fetchFuturesOrderBook pending');
        state.futuresOrderBookLoading = true;
        state.error = null;
      })
      .addCase(fetchFuturesOrderBook.fulfilled, (state, action) => {
        console.log('fetchFuturesOrderBook fulfilled', action.payload);
        state.futuresOrderBookLoading = false;
        state.futuresOrderBook = {
          bids: action.payload.bids || [],
          asks: action.payload.asks || [],
          currentPrice: action.payload.currentPrice || state.futuresOrderBook.currentPrice,
          timestamp: action.payload.timestamp,
        };
      })
      .addCase(fetchFuturesOrderBook.rejected, (state, action) => {
        console.log('fetchFuturesOrderBook rejected', action.payload);
        state.futuresOrderBookLoading = false;
        state.error = action.payload || 'Failed to fetch futures order book.';
      })
      .addCase(fetchTotalBalances.pending, (state) => {
        console.log('fetchTotalBalances pending');
        state.assetsLoading = true;
        state.error = null;
      })
      .addCase(fetchTotalBalances.fulfilled, (state, action) => {
        console.log('fetchTotalBalances fulfilled', action.payload);
        state.assetsLoading = false;
        state.totalBalances = action.payload;
        state.lastFetched.totalBalances = Date.now();
      })
      .addCase(fetchTotalBalances.rejected, (state, action) => {
        console.log('fetchTotalBalances rejected', action.payload);
        state.assetsLoading = false;
        state.error = action.payload || 'Failed to fetch total balances.';
      })
      .addCase(fetchSpotBalances.pending, (state) => {
        console.log('fetchSpotBalances pending');
        state.assetsLoading = true;
        state.error = null;
      })
      .addCase(fetchSpotBalances.fulfilled, (state, action) => {
        console.log('fetchSpotBalances fulfilled:', action.payload);
        state.assetsLoading = false;
        state.spotBalances = action.payload;
        state.walletInitialized = true;
        state.lastFetched.spotBalances = Date.now();
      })
      .addCase(fetchSpotBalances.rejected, (state, action) => {
        console.log('fetchSpotBalances rejected:', action.payload);
        state.assetsLoading = false;
        state.error = action.payload || 'Failed to fetch spot balances.';
      })
      .addCase(fetchFuturesBalances.pending, (state) => {
        console.log('fetchFuturesBalances pending');
        state.assetsLoading = true;
        state.error = null;
      })
      .addCase(fetchFuturesBalances.fulfilled, (state, action) => {
        console.log('fetchFuturesBalances fulfilled', action.payload);
        state.assetsLoading = false;
        state.futuresBalances = action.payload;
        state.lastFetched.futuresBalances = Date.now();
      })
      .addCase(fetchFuturesBalances.rejected, (state, action) => {
        console.log('fetchFuturesBalances rejected', action.payload);
        state.assetsLoading = false;
        state.error = action.payload || 'Failed to fetch futures balances.';
      })
      .addCase(fetchAIBalances.pending, (state) => {
        console.log('fetchAIBalances pending');
        state.assetsLoading = true;
        state.error = null;
      })
      .addCase(fetchAIBalances.fulfilled, (state, action) => {
        console.log('fetchAIBalances fulfilled', action.payload);
        state.assetsLoading = false;
        state.aiBalances = action.payload;
        state.lastFetched.aiBalances = Date.now();
      })
      .addCase(fetchAIBalances.rejected, (state, action) => {
        console.log('fetchAIBalances rejected', action.payload);
        state.assetsLoading = false;
        state.error = action.payload || 'Failed to fetch AI balances.';
      })
      .addCase(fetchWallet.pending, (state) => {
        console.log('fetchWallet pending');
        state.assetsLoading = true;
        state.error = null;
      })
      .addCase(fetchWallet.fulfilled, (state, action) => {
        console.log('fetchWallet fulfilled', action.payload);
        state.assetsLoading = false;
        state.walletInitialized = action.payload.status;
        state.lastFetched.wallet = Date.now();
      })
      .addCase(fetchWallet.rejected, (state, action) => {
        console.log('fetchWallet rejected', action.payload);
        state.assetsLoading = false;
        state.error = action.payload || 'Failed to fetch wallet.';
      })
      .addCase(initializeWallet.pending, (state) => {
        console.log('initializeWallet pending');
        state.assetsLoading = true;
        state.error = null;
      })
      .addCase(initializeWallet.fulfilled, (state, action) => {
        console.log('initializeWallet fulfilled', action.payload);
        state.assetsLoading = false;
        state.walletInitialized = true;
        state.walletIds = {
          ...state.walletIds,
          futuresWalletId: action.payload.futures_wallet_id || state.walletIds.futuresWalletId,
        };
      })
      .addCase(initializeWallet.rejected, (state, action) => {
        console.log('initializeWallet rejected', action.payload);
        state.assetsLoading = false;
        state.error = action.payload || 'Failed to initialize wallet.';
      })
      .addCase(fetchWalletIds.pending, (state) => {
        console.log('fetchWalletIds pending');
        state.assetsLoading = true;
        state.error = null;
      })
      .addCase(fetchWalletIds.fulfilled, (state, action) => {
        console.log('fetchWalletIds fulfilled', action.payload);
        state.assetsLoading = false;
        state.walletIds = action.payload;
        state.walletInitialized = true;
        state.lastFetched.walletIds = Date.now();
      })
      .addCase(fetchWalletIds.rejected, (state, action) => {
        console.log('fetchWalletIds rejected', action.payload);
        state.assetsLoading = false;
        state.error = action.payload || 'Failed to fetch wallet IDs.';
      })
      .addCase(createTransfer.pending, (state) => {
        console.log('createTransfer pending');
        state.assetsLoading = true;
        state.error = null;
      })
      .addCase(createTransfer.fulfilled, (state, action) => {
        console.log('createTransfer fulfilled', action.payload);
        state.assetsLoading = false;
        state.transferHistory = [action.payload, ...state.transferHistory];
      })
      .addCase(createTransfer.rejected, (state, action) => {
        console.log('createTransfer rejected', action.payload);
        state.assetsLoading = false;
        state.error = action.payload || 'Failed to create transfer.';
      })
      .addCase(getTransferHistory.pending, (state) => {
        console.log('getTransferHistory pending');
        state.assetsLoading = true;
        state.error = null;
      })
      .addCase(getTransferHistory.fulfilled, (state, action) => {
        console.log('getTransferHistory fulfilled', action.payload);
        state.assetsLoading = false;
        state.transferHistory = action.payload;
      })
      .addCase(getTransferHistory.rejected, (state, action) => {
        console.log('getTransferHistory rejected', action.payload);
        state.assetsLoading = false;
        state.error = action.payload || 'Failed to fetch transfer history.';
      })
      .addCase(fetchOpenFuturesOrders.pending, (state) => {
        console.log('fetchOpenFuturesOrders pending');
        state.openFuturesOrdersLoading = true;
        state.error = null;
      })
      .addCase(fetchOpenFuturesOrders.fulfilled, (state, action) => {
        console.log('fetchOpenFuturesOrders fulfilled', action.payload);
        state.openFuturesOrdersLoading = false;
        state.openFuturesOrders = action.payload;
      })
      .addCase(fetchOpenFuturesOrders.rejected, (state, action) => {
        console.log('fetchOpenFuturesOrders rejected', action.payload);
        state.openFuturesOrdersLoading = false;
        state.error = action.payload || 'Failed to fetch open futures orders.';
      })
      .addCase(fetchFuturesTradeHistory.pending, (state) => {
        console.log('fetchFuturesTradeHistory pending');
        state.futuresTradeHistoryLoading = true;
        state.error = null;
      })
      .addCase(fetchFuturesTradeHistory.fulfilled, (state, action) => {
        console.log('fetchFuturesTradeHistory fulfilled', action.payload);
        state.futuresTradeHistoryLoading = false;
        state.futuresTradeHistory = action.payload;
      })
      .addCase(fetchFuturesTradeHistory.rejected, (state, action) => {
        console.log('fetchFuturesTradeHistory rejected', action.payload);
        state.futuresTradeHistoryLoading = false;
        state.error = action.payload || 'Failed to fetch futures trade history.';
      })
      .addCase(fetchFuturesPrice.pending, (state) => {
        console.log('fetchFuturesPrice pending');
        state.futuresPriceLoading = true;
        state.error = null;
      })
      .addCase(fetchFuturesPrice.fulfilled, (state, action) => {
        console.log('fetchFuturesPrice fulfilled', action.payload);
        state.futuresPriceLoading = false;
        state.futuresOrderBook.currentPrice = action.payload.price;
        state.coinPrices[action.payload.symbol] = action.payload.price;
      })
      .addCase(fetchFuturesPrice.rejected, (state, action) => {
        console.log('fetchFuturesPrice rejected', action.payload);
        state.futuresPriceLoading = false;
        state.error = action.payload || 'Failed to fetch futures price.';
      })
      .addCase(placeFuturesOrder.pending, (state) => {
        console.log('placeFuturesOrder pending');
        state.assetsLoading = true;
        state.error = null;
      })
      .addCase(placeFuturesOrder.fulfilled, (state, action) => {
        console.log('placeFuturesOrder fulfilled', action.payload);
        state.assetsLoading = false;
        state.openFuturesOrders = [action.payload, ...state.openFuturesOrders];
      })
      .addCase(placeFuturesOrder.rejected, (state, action) => {
        console.log('placeFuturesOrder rejected', action.payload);
        state.assetsLoading = false;
        state.error = action.payload || 'Failed to place futures order.';
      });
  },
});

export const {
  updateCoinPrice,
  setCoinPrices,
  updateOrderBook,
  updateFuturesOrderBook,
  setOrderBookError,
  handleFuturesLiquidation,
  setCachedData,
  updateSpotBalances,
} = assetsSlice.actions;

export default assetsSlice.reducer;